/**
 * The studio's view of a video being made, derived only from what the web app already has: the
 * ivy-chat stream (as UI messages) and the project's own data. Nothing here talks to the server, and
 * nothing changes what the agent is sent. When the agent's habits change (tool names, "Build slide
 * slide-00N"), the result degrades to fewer steps and project-only scene states; it never throws.
 */
import type { UIMessage } from '../ivyChat/messageParser';

export type StudioStage = 'plan' | 'voice' | 'build' | 'review' | 'deliver';
export const STUDIO_STAGES: readonly StudioStage[] = ['plan', 'voice', 'build', 'review', 'deliver'];

export type StepKind = 'look' | 'plan' | 'storyboard' | 'narration' | 'assemble' | 'publish' | 'check' | 'export';
export type StepStatus = 'running' | 'done' | 'failed';
export type SceneStatus = 'planned' | 'building' | 'built' | 'needs-attention';

export interface StudioSlide {
  id: string;
  order: number;
  title: string;
  generationStatus?: 'pending' | 'generating' | 'completed' | 'failed';
  generatedThumbnail?: string;
  audioDuration?: number;
}

export interface StudioInput {
  messages: UIMessage[];
  isStreaming: boolean;
  slides: StudioSlide[];
  outlineApproved: boolean;
  hasManifest: boolean;
  /** Studio B: the workspace has asked for the video's manifest and it has not arrived yet (a reopen or a reload). */
  manifestLoading?: boolean;
  isPublished: boolean;
}

export interface StudioStep {
  kind: StepKind;
  status: StepStatus;
  startedAt: number;
  endedAt?: number;
  detail?: string;
  /** Studio B: when Ivy's work on it began, the later of the step before's end and your last message; the first from your send. */
  workStartedAt?: number;
}

export interface SceneState {
  id: string;
  order: number;
  title: string;
  status: SceneStatus;
  drafts: number;
  framesChecked: number;
  startedAt?: number;
  endedAt?: number;
  /** When the scene's newest draft was saved, from the builder's own save events. */
  lastDraftAt?: number;
  thumbnail?: string;
  durationSec?: number;
}

export interface StudioState {
  stage: StudioStage;
  waiting: { checkpointId: string; kind: 'plan' | 'review' | 'other' } | null;
  working: boolean;
  steps: StudioStep[];
  scenes: SceneState[];
  errors: string[];
  /** The last turn ended on a failure and nothing has happened since. */
  stopped: boolean;
  /** Studio B: from your send to the last scene built, once every scene is built. */
  builtIn?: number;
  /** Studio B: you have sent Ivy a message since the last scene was built; true when this history holds no finished build. */
  notesSinceBuild: boolean;
  /** Studio B: the video's manifest is still loading, so the video cannot play yet. */
  manifestLoading: boolean;
}

const STEP_TOOLS: Record<string, StepKind> = {
  get_design_pack: 'look',
  list_design_packs: 'look',
  extract_website_branding: 'look',
  publish_design_pack: 'look',
  update_outline: 'plan',
  sync_storyboard: 'storyboard',
  narrate_project: 'narration',
  generate_tts_all: 'narration',
  generate_batch_tts: 'narration',
  set_master_narration: 'narration',
  build_video_manifest: 'assemble',
  build_video_program: 'assemble',
  publish_video: 'publish',
  check_playback: 'check',
  export_video: 'export',
};

const STAGE_OF_STEP: Partial<Record<StepKind, StudioStage>> = {
  narration: 'voice',
  assemble: 'review',
  publish: 'review',
  check: 'review',
  export: 'deliver',
};

const DRAFT_TOOLS = new Set(['sync_slide_to_production']);
const FRAME_TOOLS = new Set(['capture_slide_screenshots', 'capture_slide_preview']);
const PREPARE_TOOLS = new Set(['prepare_slide_workspaces', 'prepare_slide_workspace', 'prepare_slide_workspace_local']);
const BUILD_TOOLS = new Set(['compile_slide', ...DRAFT_TOOLS]);
/** The agents that build or rebuild a scene. A planner, a judge or a general helper whose brief names a slide is not one. */
const BUILDER_AGENTS = new Set([
  'slide-builder', 'slide-editor', 'interactive-edu-builder', 'film-scene-builder', 'footage-scene-builder', 'kubicle-builder',
  'kubicle-editor', 'live-scene-builder', 'scene-director', 'image-to-slide', 'chart-specialist', 'animation-choreographer', 'demo-choreographer',
]);
const BUILDER_TOOLS = new Set(['Agent', 'Task']);

const shortName = (name = '') => name.replace(/^mcp__[^_]+__/, '');
const sceneIdIn = (text: unknown): string | null => (typeof text === 'string' ? text.match(/\bslide-\d{3}\b/)?.[0] ?? null : null);

/** The id of the last ```ivy-checkpoint card in Ivy's text: the question she is waiting on, if any. */
export function lastCheckpointId(text: string): string | null {
  const blocks = [...text.matchAll(/```ivy-checkpoint\s*\n([\s\S]*?)```/g)];
  const raw = blocks.at(-1)?.[1];
  if (!raw) return null;
  try {
    const id = JSON.parse(raw).id;
    return typeof id === 'string' ? id : null;
  } catch {
    return null;
  }
}

/** What a stop asks of you, from the words of its id: "planner-model-blocked" (a build stopped on our side) is no plan. */
const checkpointKind = (id: string): 'plan' | 'review' | 'other' => {
  const words = id.split(/[^a-z]+/);
  if (words.includes('outline') || words.includes('plan')) return 'plan';
  return words.includes('preview') || words.includes('review') ? 'review' : 'other';
};

const later = (a: StudioStage, b: StudioStage) => (STUDIO_STAGES.indexOf(b) > STUDIO_STAGES.indexOf(a) ? b : a);

interface Builder {
  toolUseId: string;
  sceneId: string;
  startedAt: number;
  background: boolean;
  endedAt?: number;
  failed?: boolean;
}

export function deriveStudioState(input: StudioInput): StudioState {
  const seen = new Set<string>();
  const messages = input.messages.filter((m) => {
    const key = m.toolUseId ? `${m.type}:${m.subtype ?? ''}:${m.toolUseId}` : m.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const uses = new Map<string, UIMessage>();
  const results = new Map<string, UIMessage>();
  for (const m of messages) {
    if (m.type === 'tool_use' && m.toolUseId) uses.set(m.toolUseId, m);
    if (m.type === 'tool_result' && m.toolUseId) results.set(m.toolUseId, m);
  }

  let stage: StudioStage = input.outlineApproved ? 'voice' : 'plan';
  let prepared = false;
  const steps: StudioStep[] = [];
  const stepByKind = new Map<StepKind, StudioStep>();
  const builders = new Map<string, Builder>();
  const drafts = new Map<string, number>();
  const lastDraft = new Map<string, number>();
  const frames = new Map<string, number>();
  const errors: string[] = [];
  let lastAssistantText = '';
  let answeredAfterCheckpoint = false;
  let stopped = false;
  const finished = new Set(input.slides.filter((s) => s.generationStatus === 'completed').map((s) => s.id));

  for (const m of messages) {
    if (m.type === 'chat') {
      stopped = false;
      if (m.role === 'assistant') {
        lastAssistantText = m.content;
        answeredAfterCheckpoint = false;
      } else {
        answeredAfterCheckpoint = true;
      }
      continue;
    }
    if (m.type === 'system') {
      if (m.subtype === 'error') errors.push(m.content);
      if (m.turnFailed) {
        stopped = true;
        // Its builders went with it, and history holds no end for one running in the background.
        for (const b of builders.values()) {
          if (b.endedAt) continue;
          b.endedAt = m.timestamp;
          b.failed = !finished.has(b.sceneId);
        }
      }
      if (m.subtype === 'task_notification' && m.toolUseId) {
        const b = builders.get(m.toolUseId);
        if (b && !b.endedAt) {
          b.endedAt = m.timestamp;
          b.failed = m.content !== 'completed';
        }
      }
      continue;
    }
    if (m.type !== 'tool_use' || !m.toolUseId) continue;

    const name = shortName(m.toolName);
    const toolInput = m.toolInput ?? {};
    const result = results.get(m.toolUseId);

    if (BUILDER_TOOLS.has(name)) {
      if (!BUILDER_AGENTS.has(String(toolInput.subagent_type))) continue;
      const sceneId = sceneIdIn(toolInput.prompt) ?? sceneIdIn(toolInput.description);
      if (!sceneId) continue;
      const background = toolInput.run_in_background === true;
      builders.set(m.toolUseId, {
        toolUseId: m.toolUseId,
        sceneId,
        startedAt: m.timestamp,
        background,
        ...(!background && result && { endedAt: result.timestamp, failed: !!result.isError }),
      });
      stage = later(stage, 'build');
      continue;
    }

    if (DRAFT_TOOLS.has(name) || FRAME_TOOLS.has(name)) {
      const parent = m.parentToolUseId ? uses.get(m.parentToolUseId) : undefined;
      const sceneId = (parent && (sceneIdIn(parent.toolInput?.prompt) ?? sceneIdIn(parent.toolInput?.description))) ?? sceneIdIn(toolInput.outlineSlideId);
      if (sceneId && result && !result.isError) {
        const counter = DRAFT_TOOLS.has(name) ? drafts : frames;
        counter.set(sceneId, (counter.get(sceneId) ?? 0) + 1);
        if (DRAFT_TOOLS.has(name)) lastDraft.set(sceneId, Math.max(lastDraft.get(sceneId) ?? 0, result.timestamp));
      }
    }
    if (BUILD_TOOLS.has(name)) stage = later(stage, 'build');
    if (PREPARE_TOOLS.has(name)) prepared = true;

    if (name === 'approve_outline' || (name === 'update_outline' && toolInput.approve === true)) stage = later(stage, 'voice');

    const kind = STEP_TOOLS[name];
    if (!kind || m.parentToolUseId) continue;
    if (kind === 'assemble') {
      for (const b of builders.values()) if (!b.endedAt) b.endedAt = m.timestamp;
    }
    const status: StepStatus = !result ? 'running' : result.isError ? 'failed' : 'done';
    const planned = (toolInput.outline as { slides?: unknown[] } | undefined)?.slides;
    const detail = kind === 'plan' && Array.isArray(planned) ? `${planned.length} scenes` : undefined;
    const existing = stepByKind.get(kind);
    if (existing) {
      existing.startedAt = m.timestamp;
      existing.status = status;
      existing.endedAt = result?.timestamp;
      if (detail) existing.detail = detail;
    } else {
      const step: StudioStep = { kind, status, startedAt: m.timestamp, ...(result && { endedAt: result.timestamp }), ...(detail && { detail }) };
      stepByKind.set(kind, step);
      steps.push(step);
    }
    const reached = STAGE_OF_STEP[kind];
    if (reached) stage = later(stage, reached);
  }

  const builderOf = new Map<string, Builder>();
  for (const b of builders.values()) {
    const prev = builderOf.get(b.sceneId);
    if (!prev || b.startedAt >= prev.startedAt) builderOf.set(b.sceneId, b);
  }

  const scenes: SceneState[] = [...input.slides]
    .sort((a, b) => a.order - b.order)
    .map((slide) => {
      const b = builderOf.get(slide.id);
      const fromProject: SceneStatus = slide.generationStatus === 'completed' ? 'built' : slide.generationStatus === 'failed' ? 'needs-attention' : 'planned';
      const status: SceneStatus = b ? (b.endedAt ? (b.failed ? 'needs-attention' : 'built') : 'building') : fromProject;
      return {
        id: slide.id,
        order: slide.order,
        title: slide.title,
        status,
        drafts: drafts.get(slide.id) ?? 0,
        framesChecked: frames.get(slide.id) ?? 0,
        ...(b && { startedAt: b.startedAt, endedAt: b.endedAt }),
        ...(lastDraft.has(slide.id) && { lastDraftAt: lastDraft.get(slide.id) }),
        ...(slide.generatedThumbnail && { thumbnail: slide.generatedThumbnail }),
        ...(slide.audioDuration !== undefined && { durationSec: slide.audioDuration }),
      };
    });

  if (scenes.some((s) => s.status === 'built') && stage === 'voice') stage = 'build';
  // Every scene built is the review once the video is in, or while it loads: the build in the history has ended.
  if ((input.hasManifest || input.manifestLoading) && scenes.length > 0 && scenes.every((s) => s.status === 'built')) stage = later(stage, 'review');

  const checkpointId = lastAssistantText ? lastCheckpointId(lastAssistantText) : null;
  const waiting = !input.isStreaming && checkpointId && !answeredAfterCheckpoint ? { checkpointId, kind: checkpointKind(checkpointId) } : null;
  // Preparing the scenes' workspaces is the build only once the plan is agreed: not while its review waits on you.
  if (prepared && waiting?.kind !== 'plan') stage = later(stage, 'build');
  const buildersRunning = [...builders.values()].some((b) => !b.endedAt);

  const asks = messages.filter((m) => m.type === 'chat' && m.role === 'user').map((m) => m.timestamp);
  const sentAt = asks[0];
  const ends = [...steps.map((x) => x.endedAt), ...scenes.map((x) => x.endedAt)].filter((t): t is number => t != null);
  for (const step of steps) {
    const before = (t: number) => t <= step.startedAt;
    step.workStartedAt = Math.max(sentAt ?? step.startedAt, ...asks.filter(before), ...ends.filter((t) => before(t) && t !== step.endedAt));
  }
  const allBuilt = scenes.length > 0 && scenes.every((x) => x.status === 'built' && x.endedAt != null);
  const lastBuilt = allBuilt ? Math.max(...scenes.map((x) => x.endedAt!)) : null;

  return {
    stage,
    waiting,
    working: input.isStreaming || buildersRunning,
    steps,
    scenes,
    errors,
    stopped: stopped && !input.isStreaming,
    ...(lastBuilt != null && sentAt != null && { builtIn: lastBuilt - sentAt }),
    notesSinceBuild: lastBuilt == null || asks.some((t) => t > lastBuilt),
    manifestLoading: !!input.manifestLoading,
  };
}
